import { FC, useEffect } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
  toast,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useCreateWidgetTemplate } from "@/hooks/useDashboard";
import type {
  WidgetInstance,
  WidgetTemplateConfig,
} from "@/models/dashboard/Dashboard";
import { serializeWidgetTemplateConfig } from "@/models/dashboard/Dashboard";
import { logger } from "@/lib/logger";

const formSchema = z.object({
  templateName: z.string().trim().min(1, { message: "Udfyld skabelonnavn" }),
  templateDescription: z.string(),
  defaultWidgetName: z
    .string()
    .trim()
    .min(1, { message: "Udfyld standardnavn for widget" }),
});

type FormValues = z.infer<typeof formSchema>;

const defaultsFor = (widget: WidgetInstance | null): FormValues => ({
  templateName: widget?.name ?? "",
  templateDescription: "",
  defaultWidgetName: widget?.name ?? "",
});

interface SaveAsTemplateDialogProps {
  widget: WidgetInstance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SaveAsTemplateDialog: FC<SaveAsTemplateDialogProps> = ({
  widget,
  open,
  onOpenChange,
}) => {
  const createTemplate = useCreateWidgetTemplate();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultsFor(widget),
  });

  // Re-seed on every open — the dialog stays mounted, so a discarded draft
  // would otherwise be back the next time it opens.
  useEffect(() => {
    if (open && widget) form.reset(defaultsFor(widget));
  }, [open, widget, form]);

  const handleSave = form.handleSubmit(async (values) => {
    if (!widget) return;

    const templateConfig: WidgetTemplateConfig = {
      type: widget.type,
      defaultName: values.defaultWidgetName,
      defaultSize: { w: widget.position.w, h: widget.position.h },
      config: widget.config,
    };

    try {
      await createTemplate.mutateAsync({
        name: values.templateName,
        description: values.templateDescription || undefined,
        configJson: serializeWidgetTemplateConfig(templateConfig),
      });
      onOpenChange(false);
      toast.success("Skabelon oprettet", {
        description: `"${values.templateName}" er nu tilgængelig for alle brugere.`,
      });
    } catch (e) {
      logger.error("Error creating widget template", {
        operation: "saveAsTemplate",
        error: e,
        templateName: values.templateName,
      });
      toast.error("Fejl", {
        description: "Der opstod en fejl ved oprettelse af skabelonen.",
      });
    }
  });

  const values = form.watch();
  const canSave =
    values.templateName.trim() !== "" &&
    values.defaultWidgetName.trim() !== "" &&
    !createTemplate.isPending;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={form.formState.isDirty}
    >
      <DialogHeader>
        <DialogTitle>Gem som skabelon</DialogTitle>
        <DialogDescription>
          Gem denne widgets konfiguration som en skabelon, som alle brugere i
          organisationen kan tilføje til deres dashboards.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={handleSave}>
          <div className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="templateName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Skabelonnavn</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="F.eks. Seneste bekendtgørelser"
                    />
                  </FormControl>
                  <FormDescription>
                    Dette navn vises i skabelonlisten.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="templateDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beskrivelse (valgfrit)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Kort beskrivelse af hvad denne widget viser"
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultWidgetName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Standardnavn for widget</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="F.eks. Bekendtgørelser" />
                  </FormControl>
                  <FormDescription>
                    Brugere kan ændre dette navn når de tilføjer widgetten.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter>
            <FormDialogCancel />
            <Button type="submit" disabled={!canSave}>
              {createTemplate.isPending ? "Gemmer..." : "Gem som skabelon"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default SaveAsTemplateDialog;
